// SPDX-License-Identifier: AGPL-3.0-or-later

import Accessibility from '@app/features/accessibility/state/Accessibility';
import styles from '@app/features/app/components/floating/UnreadChannelsContent.module.css';
import previewStyles from '@app/features/app/components/shared/MessagePreview.module.css';
import {Endpoints} from '@app/features/app/constants/Endpoints';
import {renderChannelStream} from '@app/features/channel/components/ChannelMessageStream';
import {ChannelNotificationSettingsDropdown} from '@app/features/channel/components/channel_header_components/ChannelNotificationSettingsDropdown';
import type {Channel} from '@app/features/channel/models/Channel';
import Channels from '@app/features/channel/state/Channels';
import * as ChannelUtils from '@app/features/channel/utils/ChannelUtils';
import GuildNSFWAgree from '@app/features/guild/state/GuildNSFWAgree';
import Guilds from '@app/features/guild/state/Guilds';
import * as InboxCommands from '@app/features/inbox/commands/InboxCommands';
import {ensureMembersForMessages} from '@app/features/messaging/commands/MessageCommands';
import {InboxMessageHeader} from '@app/features/messaging/components/popouts/InboxMessageHeader';
import {useMessageListKeyboardNavigation} from '@app/features/messaging/hooks/useMessageListKeyboardNavigation';
import {Message} from '@app/features/messaging/models/Message';
import {ChannelMessages} from '@app/features/messaging/state/ChannelMessages';
import {createChannelStream} from '@app/features/messaging/utils/MessageGroupingUtils';
import {goToMessage} from '@app/features/messaging/utils/MessageNavigator';
import LocalUserSpamOverride from '@app/features/moderation/state/LocalUserSpamOverride';
import UnreadChannels from '@app/features/notification/state/UnreadChannels';
import {conduit} from '@app/features/platform/transport/RestTransport';
import * as ReadStateCommands from '@app/features/read_state/commands/ReadStateCommands';
import ReadStates from '@app/features/read_state/state/ReadStates';
import * as ContextMenuCommands from '@app/features/ui/commands/ContextMenuCommands';
import {Scroller, type ScrollerHandle} from '@app/features/ui/components/Scroller';
import {Spinner} from '@app/features/ui/components/Spinner';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import UserGuildSettings from '@app/features/user/state/UserGuildSettings';
import UserSettings from '@app/features/user/state/UserSettings';
import {MAX_MESSAGES_PER_CHANNEL} from '@app/vendor/constants/LimitConstants';
import type {Message as WireMessage} from '@app/vendor/schema/domains/message/MessageResponseSchemas';
import {compare as compareSnowflakes, extractTimestamp} from '@app/vendor/snowflake/SnowflakeUtils';
import {useLingui} from '@lingui/react/macro';
import {BellIcon, BellSlashIcon, CaretDownIcon, CheckIcon, SparkleIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
export function getUnreadChannels(): Array<Channel> {
	const channelIds = ReadStates.getChannelIds();
	const channels: Array<Channel> = [];
	for (const channelId of channelIds) {
		const channel = Channels.getChannel(channelId);
		if (!channel) continue;
		if (!ReadStates.hasUnreadOrMentions(channel.id)) continue;
		if (UserGuildSettings.isGuildOrCategoryOrChannelMuted(channel.guildId ?? null, channel.id)) continue;
		if (GuildNSFWAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) continue;
		channels.push(channel);
	}
	return channels.sort((a, b) => {
		const aLast = ReadStates.lastMessageId(a.id) ?? a.lastMessageId ?? null;
		const bLast = ReadStates.lastMessageId(b.id) ?? b.lastMessageId ?? null;
		const aTimestamp = aLast ? extractTimestamp(aLast) : a.createdAt.getTime();
		const bTimestamp = bLast ? extractTimestamp(bLast) : b.createdAt.getTime();
		return bTimestamp - aTimestamp;
	});
}
interface ChannelPreviewData {
	channel: Channel;
	messages: Array<Message>;
	oldestUnreadMessageId: string | null;
}

const INITIAL_VISIBLE_CHANNELS = 10;

const LOAD_MORE_CHUNK = 10;
interface CacheEntry {
	cacheKey: string;
	data: ChannelPreviewData;
}

const previewCache = new Map<string, CacheEntry>();

const inFlightRequests = new Map<string, Promise<ChannelPreviewData>>();
function computeCacheKey(channelId: string): string {
	const oldestUnread =
		ReadStates.getOldestUnreadMessageId(channelId) ?? ReadStates.getVisualUnreadMessageId(channelId) ?? '';
	const ack = ReadStates.ackMessageId(channelId) ?? '';
	return `${oldestUnread}:${ack}`;
}
async function doFetchChannelPreview(channel: Channel): Promise<ChannelPreviewData> {
	if (GuildNSFWAgree.shouldShowGate({channelId: channel.id, guildId: channel.guildId ?? null})) {
		return {channel, messages: [], oldestUnreadMessageId: null};
	}
	const oldestUnreadMessageId =
		ReadStates.getOldestUnreadMessageId(channel.id) ?? ReadStates.getVisualUnreadMessageId(channel.id);
	if (oldestUnreadMessageId) {
		const response = await conduit.get<Array<WireMessage>>(Endpoints.CHANNEL_MESSAGES(channel.id), {
			query: {limit: MAX_MESSAGES_PER_CHANNEL * 2, around: oldestUnreadMessageId},
			retries: 2,
		});
		const records = (response.body ?? []).map((message) => new Message(message));
		const ordered = [...records].reverse();
		const startIndex = ordered.findIndex((message) => message.id === oldestUnreadMessageId);
		if (startIndex !== -1) {
			return {
				channel,
				messages: ordered.slice(startIndex, startIndex + MAX_MESSAGES_PER_CHANNEL),
				oldestUnreadMessageId,
			};
		}
	}
	const ackMessageId = ReadStates.ackMessageId(channel.id);
	if (!ackMessageId) {
		return {channel, messages: [], oldestUnreadMessageId: null};
	}
	const afterResponse = await conduit.get<Array<WireMessage>>(Endpoints.CHANNEL_MESSAGES(channel.id), {
		query: {limit: MAX_MESSAGES_PER_CHANNEL, after: ackMessageId},
		retries: 2,
	});
	const afterRecords = (afterResponse.body ?? []).map((message) => new Message(message));
	const sorted = [...afterRecords].sort((a, b) => compareSnowflakes(a.id, b.id));
	const computedOldestUnread = sorted[0]?.id ?? null;
	return {
		channel,
		messages: sorted,
		oldestUnreadMessageId: computedOldestUnread,
	};
}
function getOrFetchPreview(channel: Channel, cacheKey: string): Promise<ChannelPreviewData> | ChannelPreviewData {
	const cached = previewCache.get(channel.id);
	if (cached && cached.cacheKey === cacheKey) {
		return cached.data;
	}
	const inFlight = inFlightRequests.get(channel.id);
	if (inFlight) return inFlight;
	const promise = doFetchChannelPreview(channel)
		.then((data) => {
			previewCache.set(channel.id, {cacheKey, data});
			return data;
		})
		.finally(() => {
			inFlightRequests.delete(channel.id);
		});
	inFlightRequests.set(channel.id, promise);
	return promise;
}
function useChannelPreview(channel: Channel): ChannelPreviewData | null {
	const cacheKey = computeCacheKey(channel.id);
	const [data, setData] = useState<ChannelPreviewData | null>(() => {
		const cached = previewCache.get(channel.id);
		return cached && cached.cacheKey === cacheKey ? cached.data : null;
	});
	useEffect(() => {
		let active = true;
		const result = getOrFetchPreview(channel, cacheKey);
		if (result instanceof Promise) {
			const cached = previewCache.get(channel.id);
			if (!cached || cached.cacheKey !== cacheKey) {
				setData(null);
			}
			void result.then((value) => {
				if (active) setData(value);
			});
		} else {
			setData(result);
		}
		return () => {
			active = false;
		};
	}, [channel, cacheKey]);
	return data;
}
function getChannelHeadingText(channel: Channel): string {
	if (channel.isPrivate()) return ChannelUtils.getDMDisplayName(channel);
	return channel.name?.trim() || ChannelUtils.getName(channel);
}
const UnreadChannelCard = observer(function UnreadChannelCard({
	channel,
	headingId,
}: {
	channel: Channel;
	headingId: string;
}) {
	const {t} = useLingui();
	const messageDisplayCompact = UserSettings.getMessageDisplayCompact();
	const messageGroupSpacing = Accessibility.messageGroupSpacingValue;
	const mentionCount = ReadStates.getMentionCount(channel.id);
	const isCollapsed = UnreadChannels.isCollapsed(channel.id);
	const isMuted = UserGuildSettings.isGuildOrCategoryOrChannelMuted(channel.guildId ?? null, channel.id);
	const previewData = useChannelPreview(channel);
	const previewMessages = previewData?.messages ?? [];
	const oldestUnreadMessageId = previewData?.oldestUnreadMessageId ?? null;
	const spammerOverrideVersion = LocalUserSpamOverride.version;
	useEffect(() => {
		if (previewMessages.length === 0) return;
		void ensureMembersForMessages(previewMessages);
	}, [previewMessages]);
	const previewMessageState = useMemo(() => {
		const container = new ChannelMessages(channel.id);
		return previewMessages.length > 0 ? container.reset(previewMessages) : container;
	}, [channel.id, previewMessages]);
	const channelStream = useMemo(() => {
		if (!oldestUnreadMessageId || previewMessages.length === 0) return [];
		return createChannelStream({
			channel,
			messages: previewMessageState,
			oldestUnreadMessageId,
			treatSpam: true,
		});
	}, [channel, previewMessageState, previewMessages.length, oldestUnreadMessageId, spammerOverrideVersion]);
	const handleJumpToMessage = useCallback(
		(messageId: string) => {
			goToMessage(channel.id, messageId);
			InboxCommands.closeInboxAndFocusChannelTextarea(channel.id);
		},
		[channel.id],
	);
	const handleHeaderClick = useCallback(() => {
		if (oldestUnreadMessageId) {
			goToMessage(channel.id, oldestUnreadMessageId);
			InboxCommands.closeInboxAndFocusChannelTextarea(channel.id);
		}
	}, [channel.id, oldestUnreadMessageId]);
	const handleToggleCollapse = useCallback(() => {
		UnreadChannels.toggleCollapsed(channel.id);
	}, [channel.id]);
	const handleMarkAsRead = useCallback(() => {
		ReadStateCommands.ack(channel.id, true);
	}, [channel.id]);
	const handleOpenNotificationSettings = useCallback(
		(event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			event.stopPropagation();
			ContextMenuCommands.openFromElementBottomRight(event, ({onClose}) => (
				<ChannelNotificationSettingsDropdown channel={channel} onClose={onClose} />
			));
		},
		[channel],
	);
	const renderMessageActions = useCallback(
		(message: Message) => (
			<FocusRing offset={-2}>
				<button
					type="button"
					className={clsx(previewStyles.actionButton, styles.jumpButton)}
					onClick={() => handleJumpToMessage(message.id)}
				>
					{t`Jump`}
				</button>
			</FocusRing>
		),
		[handleJumpToMessage, t],
	);
	const getMessageHeadingActivate = useCallback(
		(message: Message) => () => handleJumpToMessage(message.id),
		[handleJumpToMessage],
	);
	const streamMarkup = useMemo(() => {
		if (previewMessages.length === 0) return null;
		return renderChannelStream({
			channelStream,
			messages: previewMessageState,
			channel,
			highlightedMessageId: null,
			messageDisplayCompact,
			messageGroupSpacing,
			revealedMessageId: null,
			onMessageEdit: undefined,
			onReveal: undefined,
			messageRowClassName: styles.messageRow,
			messageActionsClassName: styles.messageActions,
			renderMessageActions,
			readonlyPreview: true,
			dateDividerClassName: styles.previewDateDivider,
			suppressUnreadIndicator: true,
			getMessageHeadingActivate,
		});
	}, [
		channelStream,
		previewMessageState,
		channel,
		messageDisplayCompact,
		messageGroupSpacing,
		previewMessages.length,
		renderMessageActions,
		getMessageHeadingActivate,
	]);
	const BellComponent = isMuted ? BellSlashIcon : BellIcon;
	const channelHeading = getChannelHeadingText(channel);
	return (
		<section className={styles.channelCard} aria-labelledby={headingId} data-inbox-channel-section="" tabIndex={-1}>
			<h2 id={headingId} className={styles.srOnly}>
				<button
					type="button"
					tabIndex={-1}
					className={styles.channelHeadingButton}
					onClick={handleHeaderClick}
					disabled={!oldestUnreadMessageId}
				>
					{mentionCount > 0 ? t`${channelHeading}, ${mentionCount} mentions` : channelHeading}
				</button>
			</h2>
			<InboxMessageHeader
				channel={channel}
				onClick={handleHeaderClick}
				mentionCount={mentionCount}
				leftAdornment={
					<Tooltip text={isCollapsed ? t`Expand` : t`Collapse`} position="top">
						<FocusRing offset={-2}>
							<button
								type="button"
								className={clsx(styles.collapseButton, isCollapsed && styles.collapseButtonCollapsed)}
								onClick={handleToggleCollapse}
								aria-label={isCollapsed ? t`Expand` : t`Collapse`}
							>
								<CaretDownIcon className={styles.collapseIcon} weight="bold" />
							</button>
						</FocusRing>
					</Tooltip>
				}
				rightActions={
					<>
						<Tooltip text={t`Notification Settings`} position="top">
							<FocusRing offset={-2}>
								<button
									type="button"
									className={styles.headerIconButton}
									onClick={handleOpenNotificationSettings}
									aria-label={t`Notification Settings`}
								>
									<BellComponent className={styles.headerIcon} weight="fill" />
								</button>
							</FocusRing>
						</Tooltip>
						<Tooltip text={t`Mark as Read`} position="top">
							<FocusRing offset={-2}>
								<button
									type="button"
									className={styles.headerIconButton}
									onClick={handleMarkAsRead}
									aria-label={t`Mark as Read`}
								>
									<CheckIcon className={styles.headerIcon} weight="bold" />
								</button>
							</FocusRing>
						</Tooltip>
					</>
				}
			/>
			{!isCollapsed && (
				<div className={previewStyles.previewCard}>
					{previewData == null ? (
						<div className={styles.cardPlaceholder}>
							<Spinner />
						</div>
					) : (
						<div className={styles.messageStream}>{streamMarkup}</div>
					)}
					<div className={styles.previewFooter}>
						<FocusRing offset={-2}>
							<button
								type="button"
								className={styles.viewAllButton}
								onClick={handleHeaderClick}
								disabled={!oldestUnreadMessageId}
							>
								{t`View all unread`}
							</button>
						</FocusRing>
					</div>
				</div>
			)}
		</section>
	);
});
export const UnreadChannelsContent = observer(function UnreadChannelsContent() {
	const {t} = useLingui();
	const scrollerRef = useRef<ScrollerHandle | null>(null);
	const readStateVersion = ReadStates.version;
	const settingsVersion = UserGuildSettings.version;
	const frozenOrderRef = useRef<Map<string, number> | null>(null);
	if (frozenOrderRef.current === null) {
		frozenOrderRef.current = new Map(getUnreadChannels().map((channel, index) => [channel.id, index]));
	}
	const allUnreadChannels = useMemo(() => {
		const order = frozenOrderRef.current!;
		const current = getUnreadChannels();
		return current
			.filter((channel) => order.has(channel.id))
			.sort((a, b) => {
				const aIdx = order.get(a.id) ?? Number.POSITIVE_INFINITY;
				const bIdx = order.get(b.id) ?? Number.POSITIVE_INFINITY;
				return aIdx - bIdx;
			});
	}, [readStateVersion, settingsVersion]);
	const [loadedCount, setLoadedCount] = useState(INITIAL_VISIBLE_CHANNELS);
	const visibleChannels = useMemo(() => allUnreadChannels.slice(0, loadedCount), [allUnreadChannels, loadedCount]);
	const groupedChannels = useMemo(() => {
		const groups: Array<{key: string; label: string; channels: Array<Channel>}> = [];
		const groupIndex = new Map<string, number>();
		const directMessagesLabel = t`Direct Messages`;
		for (const channel of visibleChannels) {
			const guildId = channel.guildId ?? null;
			const key = guildId ?? '__dm__';
			let idx = groupIndex.get(key);
			if (idx === undefined) {
				const label = guildId ? (Guilds.getGuild(guildId)?.name ?? t`Unknown community`) : directMessagesLabel;
				idx = groups.length;
				groups.push({key, label, channels: []});
				groupIndex.set(key, idx);
			}
			groups[idx].channels.push(channel);
		}
		return groups;
	}, [visibleChannels, t]);
	const hasMore = loadedCount < allUnreadChannels.length;
	const handleScroll = useCallback(
		(event: React.UIEvent<HTMLDivElement>) => {
			if (!hasMore) return;
			const target = event.currentTarget;
			const scrollPercentage = (target.scrollTop + target.offsetHeight) / target.scrollHeight;
			if (scrollPercentage > 0.8) {
				setLoadedCount((current) =>
					current >= allUnreadChannels.length ? current : Math.min(current + LOAD_MORE_CHUNK, allUnreadChannels.length),
				);
			}
		},
		[hasMore, allUnreadChannels.length],
	);
	useMessageListKeyboardNavigation({
		containerRef: scrollerRef,
	});
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key !== 'd' && event.key !== 'D') return;
			if (event.ctrlKey || event.metaKey || event.altKey) return;
			const target = event.target as Element | null;
			if (target instanceof HTMLTextAreaElement || target instanceof HTMLInputElement) return;
			if (target instanceof HTMLElement && target.isContentEditable) return;
			const scrollerNode = scrollerRef.current?.getScrollerNode() ?? null;
			if (!scrollerNode) return;
			if (target && !scrollerNode.contains(target) && document.activeElement !== document.body) return;
			const sections = Array.from(scrollerNode.querySelectorAll<HTMLElement>('[data-inbox-channel-section]'));
			if (sections.length === 0) return;
			const activeSection = (
				target instanceof HTMLElement ? target.closest('[data-inbox-channel-section]') : null
			) as HTMLElement | null;
			const currentIndex = activeSection ? sections.indexOf(activeSection) : -1;
			const direction = event.shiftKey ? -1 : 1;
			let nextIndex: number;
			if (currentIndex === -1) {
				nextIndex = direction === 1 ? 0 : sections.length - 1;
			} else {
				nextIndex = (currentIndex + direction + sections.length) % sections.length;
			}
			event.preventDefault();
			event.stopPropagation();
			const nextSection = sections[nextIndex];
			nextSection.focus({preventScroll: true});
			nextSection.scrollIntoView({block: 'start', behavior: 'smooth'});
		};
		window.addEventListener('keydown', handleKeyDown, true);
		return () => window.removeEventListener('keydown', handleKeyDown, true);
	}, []);
	if (allUnreadChannels.length === 0) {
		return (
			<div className={previewStyles.emptyState}>
				<div className={previewStyles.emptyStateContent}>
					<SparkleIcon className={previewStyles.emptyStateIcon} />
					<div className={previewStyles.emptyStateTextContainer}>
						<h3 className={previewStyles.emptyStateTitle}>{t`No Unread Messages`}</h3>
						<p className={previewStyles.emptyStateDescription}>{t`You're all caught up.`}</p>
					</div>
				</div>
			</div>
		);
	}
	return (
		<Scroller key="unread-channels-scroller" className={styles.scroller} ref={scrollerRef} onScroll={handleScroll}>
			{groupedChannels.map((group) => {
				const groupHeadingId = `inbox-unread-group-${group.key}`;
				return (
					<section key={group.key} className={styles.guildGroup} aria-labelledby={groupHeadingId}>
						<h1 id={groupHeadingId} className={styles.guildGroupHeading}>
							{group.label}
						</h1>
						{group.channels.map((channel) => (
							<UnreadChannelCard key={channel.id} channel={channel} headingId={`inbox-unread-channel-${channel.id}`} />
						))}
					</section>
				);
			})}
		</Scroller>
	);
});
